<script setup>
import { defineProps, defineModel, computed, ref } from 'vue';
import WithLabel from './WithLabel.vue';

const props = defineProps({
  label: {
    type: String,
    required: true,
  },
  type: {
    type: String,
    default: 'text',
  },
  icon: {
    type: String,
    default: '',
  },
  name: {
    type: String,
    required: true,
  },
  hasError: Boolean,
  errorMessage: {
    type: String,
    default: '',
  },
  spacing: {
    type: String,
    default: 'base',
    validator: value => ['base', 'compact'].includes(value),
  },
  showPasswordToggle: {
    type: Boolean,
    default: true,
  },
});

defineOptions({
  inheritAttrs: false,
});

const model = defineModel({
  type: [String, Number],
  required: true,
});

const isPasswordVisible = ref(false);

const shouldShowPasswordToggle = computed(
  () => props.type === 'password' && props.showPasswordToggle
);

const resolvedType = computed(() =>
  shouldShowPasswordToggle.value && isPasswordVisible.value
    ? 'text'
    : props.type
);

// FluentIcon expects `icon` + `type` to compose the key, so keep base name only.
const passwordToggleIcon = computed(() =>
  isPasswordVisible.value ? 'eye-hide' : 'eye-show'
);

const togglePasswordVisibility = () => {
  if (!shouldShowPasswordToggle.value) return;

  isPasswordVisible.value = !isPasswordVisible.value;
};
</script>

<template>
  <WithLabel
    :label="label"
    :icon="icon"
    :name="name"
    :has-error="hasError"
    :error-message="errorMessage"
  >
    <template #rightOfLabel>
      <slot />
    </template>
    <div class="relative w-full">
      <input
        v-bind="$attrs"
        v-model="model"
        :type="resolvedType"
        class="block w-full border-none rounded-md shadow-sm bg-white dark:bg-n-alpha-black2 appearance-none outline outline-1 focus:outline focus:outline-1 text-n-slate-12 dark:!text-white placeholder:text-n-slate-10 dark:placeholder:!text-white/70 sm:text-sm sm:leading-6 px-3 py-3"
        :class="{
          'error outline-n-ruby-8 dark:outline-n-ruby-8 hover:outline-n-ruby-9 dark:hover:outline-n-ruby-9 disabled:outline-n-ruby-8 dark:disabled:outline-n-ruby-8':
            hasError,
          'outline-n-weak dark:outline-n-weak hover:outline-n-slate-6 dark:hover:outline-n-slate-6 focus:outline-n-brand dark:focus:outline-n-brand':
            !hasError,
          'px-3 py-3': spacing === 'base',
          'px-3 py-2 mb-0': spacing === 'compact',
          'pl-9': icon,
          'pr-11': shouldShowPasswordToggle,
        }"
      />

      <button
        v-if="shouldShowPasswordToggle"
        type="button"
        class="absolute inset-y-0 right-3 flex items-center text-black hover:text-n-slate-12 dark:text-white/70 dark:hover:text-white focus:outline-none"
        :aria-pressed="isPasswordVisible"
        aria-label="Toggle password visibility"
        @click="togglePasswordVisibility"
      >
        <fluent-icon :icon="passwordToggleIcon" size="16" />
      </button>
    </div>
  </WithLabel>
</template>
